// Resources Explorer data layer helpers.
// Normalizes the existing GetFileResourcesResponse shape
// ({ folders, documents, images, videos }) returned by the `resources/getResources`
// and `resources/getResourcesByFolderId` store actions into a single flat node map.
//
// Node shape:
// { id, name, parentId, kind: 'folder'|'file', resourceType, resourceId, isEditable, isArchived,
//   folderCount, fileCount, fileType, modified, modifiedBy,
//   // folder-only extras
//   folderType, propertyId, childCount, isPropertyGroup, loaded, loading, childIds,
//   // file-only extras
//   mediaType: 'Document'|'Image'|'Video', raw }

export var ROOT_ID = 'root'
export var EXPLORER_TOGGLE_KEY = 'resourcesExplorerV2'
export var RECENTS_KEY = 'resourcesRecentFolders'
export var RECENTS_MAX = 8

var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Spr2DateStamp is an object; elsewhere in the app it is read via `.dateTimeUtc`
// (see RoutineTaskInstances.vue). Plain strings are accepted too. Returns a Date or null.
export function stampToDate (v) {
  if (!v) return null
  var raw = v
  if (typeof v === 'object' && !(v instanceof Date)) {
    raw = firstDefined(v, ['dateTimeUtc', 'utc', 'dateTime', 'value'])
    if (!raw) return null
  }
  if (typeof raw === 'string' && /T\d\d:\d\d/.test(raw) && !/(Z|[+-]\d\d:?\d\d)$/.test(raw)) {
    raw = raw + 'Z' // UTC without an offset marker
  }
  var d = raw instanceof Date ? raw : new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

// uploadedByPerson may be a display string or a person object.
export function personName (v) {
  if (!v) return null
  if (typeof v === 'string') return v
  if (typeof v === 'object') {
    var full = firstDefined(v, ['name', 'fullName', 'displayName'])
    if (full) return String(full)
    var joined = [v.firstName, v.lastName].filter(function (x) { return !!x }).join(' ')
    return joined || null
  }
  return null
}

function latestVersion (f) {
  var versions = Array.isArray(f.versions) ? f.versions : []
  var best = null
  var bestTime = -1
  versions.forEach(function (ver) {
    var d = stampToDate(ver && ver.dateUploaded)
    var t = d ? d.getTime() : -1
    if (best === null || t > bestTime || (t === bestTime && (ver.number || 0) > (best.number || 0))) {
      best = ver
      bestTime = t
    }
  })
  return best
}

var CONTENT_TYPE_LABELS = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'text/plain': 'TXT',
  'text/csv': 'CSV'
}

function typeLabel (f, mediaType) {
  var ext = fileExtension(f.fileName || '') || fileExtension(f.name || '')
  if (ext) return ext
  var ct = String(f.contentType || '').toLowerCase()
  if (CONTENT_TYPE_LABELS[ct]) return CONTENT_TYPE_LABELS[ct]
  if (ct.indexOf('/') !== -1) {
    var sub = ct.split('/')[1].split(';')[0]
    if (sub && sub.length <= 5) return sub.toUpperCase()
  }
  return mediaType === 'Image' ? 'IMAGE' : (mediaType === 'Video' ? 'VIDEO' : 'FILE')
}

function firstDefined (obj, keys) {
  for (var i = 0; i < keys.length; i++) {
    var v = obj[keys[i]]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return null
}

export function createRootNode () {
  return {
    id: ROOT_ID,
    name: 'Resources',
    parentId: null,
    kind: 'folder',
    resourceType: 'Company',
    resourceId: null,
    isEditable: false,
    isArchived: false,
    folderCount: null,
    fileCount: null,
    fileType: null,
    modified: null,
    modifiedBy: null,
    folderType: 'Company',
    propertyId: null,
    childCount: null,
    isPropertyGroup: false,
    loaded: false,
    loading: false,
    childIds: []
  }
}

export function normalizeFolder (f, parentId) {
  var folderType = f.folderType || 'Company'
  var isProperty = folderType.toLowerCase() === 'property'
  var propertyId = f.propertyId === undefined ? null : f.propertyId
  return {
    id: String(f.id),
    name: f.name || '',
    parentId: parentId,
    kind: 'folder',
    resourceType: isProperty ? 'Property' : 'Company',
    resourceId: propertyId,
    isEditable: f.isEditable === true,
    isArchived: f.isArchived === true,
    folderCount: null,
    fileCount: null,
    fileType: null,
    // FolderForDisplay: Modified = dateUpdated (fallback dateCreated); By = editedByPersonName (fallback createdByPersonName)
    modified: stampToDate(firstDefined(f, ['dateUpdated', 'dateCreated'])),
    modifiedBy: personName(firstDefined(f, ['editedByPersonName', 'createdByPersonName'])),
    folderType: folderType,
    propertyId: propertyId,
    // Same source as the badge on the classic tiles (folder.childCount)
    childCount: typeof f.childCount === 'number' ? f.childCount : null,
    // The special "Properties" folder (see Resources.vue isSpecialFolder)
    isPropertyGroup: isProperty && propertyId === null && f.isEditable === false,
    loaded: false,
    loading: false,
    childIds: [],
    raw: f
  }
}

export function fileExtension (name) {
  if (!name || name.indexOf('.') === -1) return ''
  return name.split('.').pop().toUpperCase()
}

export function normalizeFile (f, mediaType, parentNode) {
  var fileName = f.fileName || f.name || ''
  var latest = latestVersion(f)
  var propertyId = f.propertyId === undefined ? null : f.propertyId
  return {
    id: 'file:' + mediaType + ':' + f.id,
    name: f.name || fileName,
    parentId: parentNode.id,
    kind: 'file',
    resourceType: propertyId === null ? 'Company' : 'Property',
    resourceId: propertyId,
    isEditable: f.isEditable === true,
    isArchived: f.isArchived === true,
    folderCount: null,
    fileCount: null,
    fileType: typeLabel(f, mediaType),
    // FileResourceForDisplay has no size field. Modified = latest versions[].dateUploaded; By = uploadedByPerson
    modified: latest ? stampToDate(latest.dateUploaded) : null,
    modifiedBy: personName(f.uploadedByPerson),
    mediaType: mediaType,
    raw: f
  }
}

// Converts an API response into { folders: [node], files: [node] } (archived hidden unless showArchived).
// Mirrors Resources.vue: files are not shown at the root or in the special Properties folder.
export function normalizeResponse (result, parentNode, showArchived) {
  var res = result || {}
  var folders = (res.folders || []).map(function (f) { return normalizeFolder(f, parentNode.id) })
  var files = []
  var showFiles = parentNode.id !== ROOT_ID && !parentNode.isPropertyGroup
  if (showFiles) {
    (res.documents || []).forEach(function (d) { files.push(normalizeFile(d, 'Document', parentNode)) })
    ;(res.images || []).forEach(function (d) { files.push(normalizeFile(d, 'Image', parentNode)) })
    ;(res.videos || []).forEach(function (d) { files.push(normalizeFile(d, 'Video', parentNode)) })
  }
  function visible (n) { return showArchived === true || !n.isArchived }
  return {
    folders: folders.filter(visible),
    files: files.filter(visible)
  }
}

export function compareByName (a, b) {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
}

function toTime (v) {
  if (!v) return 0
  var d = stampToDate(v)
  return d ? d.getTime() : 0
}

// Folders first, then files. sortKey: 'name' | 'modified' (newest first)
export function sortNodes (list, sortKey) {
  var folders = list.filter(function (n) { return n.kind === 'folder' })
  var files = list.filter(function (n) { return n.kind === 'file' })
  var cmp = compareByName
  if (sortKey === 'modified') {
    cmp = function (a, b) {
      var d = toTime(b.modified) - toTime(a.modified)
      return d !== 0 ? d : compareByName(a, b)
    }
  }
  return folders.sort(cmp).concat(files.sort(cmp))
}

function plural (n, word) {
  return n + ' ' + word + (n === 1 ? '' : 's')
}

// 'short' => "Jun 2" (adds year if not current year), 'long' => "Oct 9, 2026"
export function formatDate (v, style) {
  if (!v) return ''
  var d = stampToDate(v)
  if (!d) return ''
  var base = MONTHS[d.getMonth()] + ' ' + d.getDate()
  if (style === 'short' && d.getFullYear() === new Date().getFullYear()) return base
  return base + ', ' + d.getFullYear()
}

// "Dana Robinson" => "Dana R."
export function shortPersonName (v) {
  if (!v || typeof v !== 'string') return ''
  var parts = v.trim().split(/\s+/)
  if (parts.length < 2) return parts[0] || ''
  return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.'
}

// Contents / meta line for a folder: "4 files · 2 folders" once loaded, else total from childCount.
export function folderMeta (node) {
  if (node.loaded) {
    var parts = []
    if (node.fileCount > 0 || node.folderCount === 0) parts.push(plural(node.fileCount || 0, 'file'))
    if (node.folderCount > 0) parts.push(plural(node.folderCount, 'folder'))
    return parts.join(' · ')
  }
  if (node.childCount === null || node.childCount === undefined) return ''
  if (node.childCount === 0) return 'Empty'
  return plural(node.childCount, 'item')
}

export function fileMeta (node, dateStyle) {
  var parts = [node.fileType]
  if (dateStyle) {
    var date = formatDate(node.modified, dateStyle)
    if (date) parts.push(date)
  }
  return parts.join(' · ')
}

export function nodeMeta (node, dateStyle) {
  return node.kind === 'folder' ? folderMeta(node) : fileMeta(node, dateStyle)
}

// Ancestor chain (root first) for a node id in the node map.
export function pathOf (nodes, id) {
  var chain = []
  var guard = 0
  var cur = nodes[id]
  while (cur && guard < 100) {
    chain.unshift(cur)
    cur = cur.parentId ? nodes[cur.parentId] : null
    guard++
  }
  return chain
}

export function readToggle () {
  try {
    return window.localStorage.getItem(EXPLORER_TOGGLE_KEY) === 'true'
  } catch (e) {
    return false
  }
}

export function writeToggle (on) {
  try {
    window.localStorage.setItem(EXPLORER_TOGGLE_KEY, on ? 'true' : 'false')
  } catch (e) { /* ignore */ }
}

export function readRecents () {
  try {
    var list = JSON.parse(window.localStorage.getItem(RECENTS_KEY) || '[]')
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

// entry: { id, rt, rid, label }
export function pushRecent (entry) {
  var list = readRecents().filter(function (r) { return r && r.id !== entry.id })
  list.unshift(entry)
  list = list.slice(0, RECENTS_MAX)
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list))
  } catch (e) { /* ignore */ }
  return list
}

// ---------- F2: folder editing rules ----------

// System folders (isEditable === false: property roots, default folders, "Properties") cannot be renamed/moved.
export function canRenameOrMove (node) {
  return !!node && node.kind === 'folder' && node.id !== ROOT_ID && node.isEditable === true
}

// Creating inside system folders is allowed, except directly under the company "Properties" folder.
export function canCreateIn (node) {
  return !!node && node.kind === 'folder' && !node.isPropertyGroup
}

// F4b: files can be renamed (PUT .../resources/files/{id}/rename)
export function canRenameFile (node) {
  return !!node && node.kind === 'file' && !!node.raw
}

// Folder or file rename
export function canRenameItem (node) {
  return canRenameOrMove(node) || canRenameFile(node)
}

export function hasFolderActions (node) {
  return canRenameOrMove(node) || canCreateIn(node)
}

// Id used by the API for a parent folder (company root => null).
export function apiFolderId (node) {
  return !node || node.id === ROOT_ID ? null : node.id
}

// Property folders route through /properties/{propertyId}/...
export function apiPropertyId (node) {
  if (!node || node.resourceType !== 'Property') return null
  return node.propertyId === null || node.propertyId === undefined ? null : node.propertyId
}

function isSameOrDescendant (nodes, id, ancestorId) {
  var guard = 0
  var cur = nodes[id]
  while (cur && guard < 100) {
    if (cur.id === ancestorId) return true
    cur = cur.parentId ? nodes[cur.parentId] : null
    guard++
  }
  return false
}

// Destination picker rule for moving `moving`. Returns true when `dest` cannot be chosen.
export function isMoveDestinationDisabled (nodes, moving, dest) {
  if (!moving || !dest || dest.kind !== 'folder') return true
  if (isSameOrDescendant(nodes, dest.id, moving.id)) return true
  if (dest.id === (moving.parentId || ROOT_ID)) return true
  if (dest.isPropertyGroup) return true
  if (moving.resourceType === 'Property') {
    if (dest.resourceType !== 'Property') return true
    return String(dest.propertyId) !== String(moving.propertyId)
  }
  return dest.resourceType !== 'Company'
}

// Trims each segment of a "/"-nested path. Returns '' when any segment is empty.
export function cleanFolderPath (v) {
  var segs = String(v || '').split('/').map(function (s) { return s.trim() })
  if (segs.some(function (s) { return !s })) return ''
  return segs.join('/')
}

// ---------- F3: archive / restore / delete / multi-select ----------

export var SHOW_ARCHIVED_KEY = 'resourcesShowArchived'
export var DELETE_FOLDER_REASON = 'Archive this folder first, or empty it'
export var DELETE_FILE_REASON = 'Archive this file first'
export var SYSTEM_FOLDER_REASON = 'System folders can\'t be changed'

export function readShowArchived () {
  try {
    return window.localStorage.getItem(SHOW_ARCHIVED_KEY) === 'true'
  } catch (e) {
    return false
  }
}

export function writeShowArchived (on) {
  try {
    window.localStorage.setItem(SHOW_ARCHIVED_KEY, on ? 'true' : 'false')
  } catch (e) { /* ignore */ }
}

// Folders: same rule as rename/move (no system folders). Files: always.
export function canArchive (node) {
  if (!node) return false
  return node.kind === 'file' || canRenameOrMove(node)
}

export function canMoveItem (node) {
  return canArchive(node)
}

// Multi-select excludes system folders
export function canSelect (node) {
  return canArchive(node)
}

// '' when the item can be deleted, otherwise the reason it cannot.
export function deleteBlockReason (node) {
  if (!node) return 'Not available'
  if (node.kind === 'file') return node.isArchived ? '' : DELETE_FILE_REASON
  if (!canRenameOrMove(node)) return SYSTEM_FOLDER_REASON
  if (node.isArchived) return ''
  var empty = node.childCount === 0 && (node.childIds || []).length === 0
  return empty ? '' : DELETE_FOLDER_REASON
}

// Delete confirmation text for a single item
export function deleteConfirmText (node) {
  if (node.kind === 'file') return 'Delete \'' + node.name + '\'? This can\'t be undone.'
  var known = null
  if (node.loaded && node.folderCount === 0) known = node.fileCount || 0
  else if (node.childCount === 0) known = 0
  var what = known === null ? ' and everything in it' : (known > 0 ? ' and its ' + plural(known, 'file') : '')
  return 'Delete \'' + node.name + '\'' + what + '? This can\'t be undone.'
}

// Id the file endpoints expect (node ids are prefixed "file:<Type>:")
export function fileApiId (node) {
  return node && node.raw ? node.raw.id : null
}

export function filePropertyId (node) {
  if (!node || !node.raw) return null
  var p = node.raw.propertyId
  return p === undefined || p === '' ? null : p
}

// Destination rule for moving a file: a folder in the same resource, not its current folder.
export function isFileMoveDestinationDisabled (nodes, file, dest) {
  if (!file || !dest || dest.kind !== 'folder') return true
  if (dest.id === ROOT_ID || dest.isPropertyGroup) return true
  if (dest.id === file.parentId) return true
  var pid = filePropertyId(file)
  if (pid !== null) {
    if (dest.resourceType !== 'Property') return true
    return String(dest.propertyId) !== String(pid)
  }
  return dest.resourceType !== 'Company'
}

// Disabled when invalid for ANY of the items being moved. Archived folders are not destinations.
export function isMoveDestinationDisabledFor (nodes, items, dest) {
  if (!dest || dest.isArchived) return true
  return items.some(function (item) {
    return item.kind === 'file'
      ? isFileMoveDestinationDisabled(nodes, item, dest)
      : isMoveDestinationDisabled(nodes, item, dest)
  })
}

// Menu / action sheet entries for a folder or file.
// { key, title, sub, icon, disabled, danger, divider }
export function itemActions (node) {
  var out = []
  if (!node) return out
  var archived = node.isArchived === true
  var delReason = deleteBlockReason(node)
  if (node.kind === 'folder') {
    var editable = canRenameOrMove(node)
    if (editable) out.push({ key: 'rename', title: 'Rename', icon: 'fas fa-pen' })
    if (canCreateIn(node) && !archived) out.push({ key: 'new-subfolder', title: 'New subfolder', icon: 'fas fa-folder-plus' })
    if (editable) out.push({ key: 'move', title: 'Move to…', sub: 'Pick a destination from the tree', icon: 'fas fa-arrow-right' })
    out.push({ key: 'copy-structure', title: 'Copy structure to other properties…', sub: 'Coming soon', icon: 'far fa-copy', disabled: true, divider: true })
    if (archived) {
      out.push({ key: 'restore', title: 'Restore', sub: 'Make it visible again', icon: 'fas fa-undo', disabled: !editable })
    } else {
      out.push({ key: 'archive', title: 'Archive', sub: editable ? 'Hidden from everyone; restorable' : SYSTEM_FOLDER_REASON, icon: 'fas fa-archive', disabled: !editable })
    }
    out.push({ key: 'delete', title: 'Delete', sub: delReason || 'Permanently remove', icon: 'far fa-trash-alt', disabled: !!delReason, danger: !delReason })
    return out
  }
  out.push({ key: 'rename', title: 'Rename', icon: 'fas fa-pen' })
  out.push({ key: 'versions', title: 'Version history', sub: 'Download or restore earlier versions', icon: 'fas fa-history' })
  out.push({ key: 'move', title: 'Move to…', sub: 'Pick a destination folder', icon: 'fas fa-arrow-right' })
  if (archived) {
    out.push({ key: 'restore', title: 'Restore', sub: 'Make it visible again', icon: 'fas fa-undo', divider: true })
  } else {
    out.push({ key: 'archive', title: 'Archive', sub: 'Hidden from everyone; restorable', icon: 'fas fa-archive', divider: true })
  }
  out.push({ key: 'delete', title: 'Delete', sub: delReason || 'Permanently remove', icon: 'far fa-trash-alt', disabled: !!delReason, danger: !delReason })
  return out
}

export function hasItemActions (node) {
  return !!node && (node.kind === 'file' || hasFolderActions(node))
}

// ---------- F4a: uploads ----------

// '' when files can be uploaded into the folder, otherwise why not.
// Files are never shown at the root or in the special "Properties" folder (see normalizeResponse /
// classic Resources.vue, which hides "Add Document" at the root), and archived folders are read-only.
export function uploadBlockReason (node) {
  if (!node || node.kind !== 'folder') return 'Files can only be uploaded into a folder'
  if (node.id === ROOT_ID) return 'Choose a folder to upload into'
  if (node.isPropertyGroup) return 'Files can\'t be uploaded into the Properties folder. Open a property first'
  if (node.isArchived) return 'Files can\'t be uploaded into an archived folder'
  return ''
}

export function canUploadTo (node) {
  return uploadBlockReason(node) === ''
}

// "Parent › Folder" label for a destination
export function folderLabel (nodes, id) {
  return pathOf(nodes, id)
    .filter(function (n) { return n.id !== ROOT_ID })
    .slice(-2)
    .map(function (n) { return n.name })
    .join(' › ')
}
